import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { ApiResponse, PaginatedResponse } from '@demoops/shared-types';
import type { ChecklistItem } from '../inspectionChecklist';
import type {
  Equipment, EquipmentInspection, EquipmentReading, EquipmentWriteData, CreateReadingData, EquipmentStatus,
  CommandCenterData, EquipmentEvent,
  EquipmentFilterOptions, MountAttachmentData, UnmountAttachmentData, ProjectEquipmentHours,
  ResolvedCombo,
} from '../types';

interface EquipmentFilters {
  search?: string;
  status?: string;
  type?: string;
  /** Asset kind discriminator — machine | attachment (attachments rework). */
  kind?: string;
  /** Machine category chip (filter-options values, e.g. "Excavator"). */
  category?: string;
  attachment_type?: string;
  /** Equipment whose ACTIVE assignment is this project (live since e8a1a38). */
  project_id?: string;
  active_only?: boolean;
  /** Only equipment that may be sent to a job — excludes the office items. */
  assignable_only?: boolean;
  /** Machines first, then attachments, asset number within each group. */
  group_by_kind?: boolean;
  /**
   * Rentals (2026-08-27). ABSENT means owned — every existing caller of this
   * hook keeps the fleet it always had. 'all' is for the places costs are
   * coded, where a hire (returned or not) must be pickable.
   */
  ownership?: 'owned' | 'rented' | 'all';
  /** Rentals tab only: show hires that have gone back. */
  include_returned?: boolean;
  /**
   * Company pickups and service vehicles (2026-09-02). ABSENT means hidden —
   * they are equipment rows only so an invoice can be EQ-coded to them. The
   * invoice EQ picker is the one caller that sets this.
   */
  include_vehicles?: boolean;
  /**
   * Manlifts (2026-09-03): decorate every row with `preuse_today` — today's
   * latest pre-use inspection. Only the Manlifts view asks; absent, no list
   * changes shape.
   */
  include_preuse?: boolean;
  page?: number;
  per_page?: number;
}

export const equipmentKeys = {
  all: ['equipment'] as const,
  lists: () => [...equipmentKeys.all, 'list'] as const,
  list: (f: EquipmentFilters) => [...equipmentKeys.lists(), f] as const,
  detail: (id: string) => [...equipmentKeys.all, 'detail', id] as const,
  readings: (id: string) => [...equipmentKeys.all, 'readings', id] as const,
  commandCenter: () => [...equipmentKeys.all, 'commandCenter'] as const,
  events: (id: string, perPage: number) => [...equipmentKeys.all, 'events', id, perPage] as const,
  moves: (id: string) => [...equipmentKeys.all, 'moves', id] as const,
  /** Vic's inbox (GET /equipment/inbox, fleet step 3) — every move write invalidates it. */
  inbox: () => [...equipmentKeys.all, 'inbox'] as const,
  /** A job's open requests (GET /equipment-requests?project_id=) — the project Equipment tab's Requests block. */
  requests: (projectId: string) => [...equipmentKeys.all, 'requests', projectId] as const,
  filterOptions: () => [...equipmentKeys.all, 'filterOptions'] as const,
  /** The fleet board (step 4) — one cached board per filter set; boards()
   *  is the prefix invalidateAfterMove sweeps. */
  boards: () => [...equipmentKeys.all, 'board'] as const,
  board: (f: object) => [...equipmentKeys.boards(), f] as const,
  /** A hire's two condition reports + the rental shot list (fleet step 5). */
  conditionReports: (id: string) => [...equipmentKeys.all, 'conditionReports', id] as const,
  /** The fleet timeline (step 4; its tab lands with the timeline task). */
  timelines: () => [...equipmentKeys.all, 'timeline'] as const,
  timeline: (f: object) => [...equipmentKeys.timelines(), f] as const,
  /** Samsara phase 2 (2026-09-02): one machine's tracker summary and its
   *  location trail; the fleet-wide feeds live under fleet() so a sync can
   *  sweep them in one invalidation. */
  samsara: (id: string) => [...equipmentKeys.all, 'samsara', id] as const,
  samsaraTrail: (id: string, range: string) => [...equipmentKeys.all, 'samsara', id, 'trail', range] as const,
  fleet: () => [...equipmentKeys.all, 'fleet'] as const,
  fleetTelemetry: () => [...equipmentKeys.fleet(), 'telemetry'] as const,
  fleetGateways: () => [...equipmentKeys.fleet(), 'gateways'] as const,
  fleetReconciliation: (week: string, projectId?: string) =>
    [...equipmentKeys.fleet(), 'reconciliation', week, projectId ?? ''] as const,
};

/** Distinct category / attachment-type values with counts for the list filter
 *  chips. The fleet's shape changes rarely — 5m staleTime so tab flips and
 *  chip taps never re-hit the aggregate query. */
export function useEquipmentFilterOptions() {
  return useQuery({
    queryKey: equipmentKeys.filterOptions(),
    queryFn: async () =>
      (await api.get<ApiResponse<EquipmentFilterOptions>>('/api/v1/equipment/filter-options')).data,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Key for a project's Hours & billing rollup. Lives under the ['projects']
 *  prefix (it's project-scoped data); useReplaceUsageSegments invalidates it
 *  by predicate since segment saves move these numbers. */
export const projectEquipmentHoursKey = (projectId: string) =>
  ['projects', projectId, 'equipmentHours'] as const;

/** Per-machine hours (+ charges for financials holders) rolled up from a
 *  project's usage segments — GET /projects/{id}/equipment-hours, gate
 *  equipment.view. Charge keys are absent without projects.view_financials. */
export function useProjectEquipmentHours(projectId: string | undefined) {
  return useQuery({
    queryKey: projectEquipmentHoursKey(projectId ?? ''),
    queryFn: async () =>
      (await api.get<ApiResponse<ProjectEquipmentHours>>(`/api/v1/projects/${projectId}/equipment-hours`)).data,
    enabled: !!projectId,
    staleTime: 60_000,
    retry: false,
  });
}

/** Fleet command-center rollup (attachments rework Phase 6). Counts move on a
 *  minute cadence at most — 60s staleTime keeps tab flips from re-hitting the
 *  aggregate queries. */
export function useCommandCenter() {
  return useQuery({
    queryKey: equipmentKeys.commandCenter(),
    queryFn: async () =>
      (await api.get<ApiResponse<CommandCenterData>>('/api/v1/equipment/command-center')).data,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Append-only asset event ledger, newest first. "Load more" grows per_page
 * rather than walking pages — the list is one screen, and a growing window
 * can never drop rows when new events land between fetches.
 */
export function useEquipmentEvents(id: string | undefined, perPage = 25) {
  return useQuery({
    queryKey: equipmentKeys.events(id ?? '', perPage),
    queryFn: async () =>
      api.get<PaginatedResponse<EquipmentEvent>>(`/api/v1/equipment/${id}/events?per_page=${perPage}`),
    enabled: !!id,
    placeholderData: (prev) => prev, // keep rows visible while the window grows
    retry: false,
  });
}

/**
 * `enabled` (default true) is for typeaheads that must not fire until there is
 * something to search; `keepPreviousData` keeps the last answer on screen
 * while the next one is out, so a settled list does not blank between keys.
 */
export function useEquipmentList(filters: EquipmentFilters = {}, enabled = true, opts: { keepPreviousData?: boolean } = {}) {
  return useQuery({
    queryKey: equipmentKeys.list(filters),
    queryFn: async () => {
      const q = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== '') q.append(k, String(v)); });
      return api.get<PaginatedResponse<Equipment>>(`/api/v1/equipment?${q.toString()}`);
    },
    enabled,
    placeholderData: opts.keepPreviousData ? keepPreviousData : undefined,
    retry: false, // don't hammer if the endpoint isn't up yet
  });
}

/**
 * Every id matching the current filter, for "select all matching" on the label
 * sheet (Q1). Fetched on demand — `enabled` stays false until the person asks
 * for it, so the ordinary list view never pulls the whole fleet.
 *
 * One page big enough to hold the fleet (297 assets today) rather than walking
 * the pager: the list endpoint passes `per_page` straight to `paginate()` with
 * no cap. `labelIds` is asserted against `meta.total` at the call site so a
 * short read can never masquerade as a complete selection.
 */
export function useEquipmentLabelIds(filters: EquipmentFilters, enabled: boolean) {
  const all = { ...filters, page: 1, per_page: 1000 };
  return useQuery({
    queryKey: [...equipmentKeys.lists(), 'labelIds', all],
    queryFn: async () => {
      const q = new URLSearchParams();
      Object.entries(all).forEach(([k, v]) => { if (v !== undefined && v !== '') q.append(k, String(v)); });
      const res = await api.get<PaginatedResponse<Equipment>>(`/api/v1/equipment?${q.toString()}`);
      return { ids: res.data.map((e) => e.id), total: res.meta?.total ?? res.data.length };
    },
    enabled,
    retry: false,
  });
}

export function useEquipment(id: string | undefined) {
  return useQuery({
    queryKey: equipmentKeys.detail(id ?? ''),
    queryFn: async () => (await api.get<ApiResponse<Equipment>>(`/api/v1/equipment/${id}`)).data,
    enabled: !!id,
    retry: false,
  });
}

/**
 * Resolve a printed label code to the asset it identifies — GET /api/v1/e/{code}
 * (E1 resolver, gate equipment.view). Case-insensitive server-side; the key is
 * uppercased so `y13qh7` and `Y13QH7` share a cache entry. A 404 means "no
 * such code" (zero-exposure) — `retry: false` lets it settle immediately.
 */
export function useResolveEquipmentCode(code: string | undefined) {
  return useQuery({
    queryKey: [...equipmentKeys.all, 'resolve', (code ?? '').toUpperCase()],
    queryFn: async () =>
      (await api.get<ApiResponse<ResolvedCombo>>(`/api/v1/e/${encodeURIComponent(code ?? '')}`)).data,
    enabled: !!code,
    staleTime: 60_000,
    retry: false,
  });
}

export function useEquipmentReadings(id: string | undefined) {
  return useQuery({
    queryKey: equipmentKeys.readings(id ?? ''),
    queryFn: async () => (await api.get<ApiResponse<EquipmentReading[]>>(`/api/v1/equipment/${id}/readings`)).data,
    enabled: !!id,
    retry: false,
  });
}

export function useCreateEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: EquipmentWriteData) => (await api.post<ApiResponse<Equipment>>('/api/v1/equipment', data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: equipmentKeys.lists() }),
  });
}

export function useUpdateEquipment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: EquipmentWriteData) => (await api.put<ApiResponse<Equipment>>(`/api/v1/equipment/${id}`, data)).data,
    onSuccess: (eq) => {
      qc.setQueryData(equipmentKeys.detail(id), eq);
      qc.invalidateQueries({ queryKey: equipmentKeys.lists() });
    },
  });
}

export function useDeleteEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/api/v1/equipment/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: equipmentKeys.lists() }),
  });
}

export function useSetEquipmentStatus(id: string) {
  const qc = useQueryClient();
  return useMutation({
    // QA-074: Out of service carries why; nothing else sends a reason.
    mutationFn: async ({ status, status_reason }: { status: EquipmentStatus; status_reason?: string }) =>
      (await api.post<ApiResponse<Equipment>>(`/api/v1/equipment/${id}/status`, status_reason ? { status, status_reason } : { status })).data,
    onSuccess: (eq) => {
      qc.setQueryData(equipmentKeys.detail(id), eq);
      qc.invalidateQueries({ queryKey: equipmentKeys.lists() });
    },
  });
}

export function useCreateReading(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateReadingData) => (await api.post<ApiResponse<EquipmentReading>>(`/api/v1/equipment/${id}/readings`, data)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: equipmentKeys.readings(id) });
      qc.invalidateQueries({ queryKey: equipmentKeys.detail(id) });
    },
  });
}

/**
 * Mount (or swap to) an attachment — POST /equipment/{machineId}/mount, gate
 * equipment.record_usage OR equipment.approve_moves. Swapping is the same call:
 * mounting over an existing attachment auto-unmounts it server-side. The
 * response is the MACHINE (with current_attachment), seeded straight into its
 * detail cache; the broad ['equipment'] invalidation then refreshes the
 * attachment's detail (mounted_on), both list tabs, the command center, and
 * every compatible-attachments feed — availability changes for OTHER machines
 * too, and all those keys live under the equipment prefix by design.
 */
export function useMountAttachment(machineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: MountAttachmentData) =>
      (await api.post<ApiResponse<Equipment>>(`/api/v1/equipment/${machineId}/mount`, data)).data,
    onSuccess: (machine) => {
      qc.setQueryData(equipmentKeys.detail(machineId), machine);
      qc.invalidateQueries({ queryKey: equipmentKeys.all });
    },
  });
}

/** Take the current attachment off — POST /equipment/{machineId}/unmount
 *  (no-op when bare). Same gate + invalidation as useMountAttachment. */
export function useUnmountAttachment(machineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: UnmountAttachmentData = {}) =>
      (await api.post<ApiResponse<Equipment>>(`/api/v1/equipment/${machineId}/unmount`, data)).data,
    onSuccess: (machine) => {
      qc.setQueryData(equipmentKeys.detail(machineId), machine);
      qc.invalidateQueries({ queryKey: equipmentKeys.all });
    },
  });
}

/** Change kind — machine ⇄ attachment (POST /equipment/{id}/kind, 2026-08-28). */
export function useChangeKind(equipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { kind: 'machine' | 'attachment' }) =>
      (await api.post<ApiResponse<Equipment>>(`/api/v1/equipment/${equipmentId}/kind`, data)).data,
    onSuccess: (eq) => {
      qc.setQueryData(equipmentKeys.detail(equipmentId), eq);
      qc.invalidateQueries({ queryKey: equipmentKeys.all });
    },
  });
}

/** Inspection history (walk-arounds + service records). */
export function useEquipmentInspections(id: string | undefined) {
  return useQuery({
    queryKey: [...equipmentKeys.all, 'inspections', id ?? ''],
    queryFn: async () =>
      (await api.get<ApiResponse<EquipmentInspection[]>>(`/api/v1/equipment/${id}/inspections`)).data,
    enabled: !!id,
    retry: false,
  });
}

/** Server-driven checklist template (BE-088). Resolves the active template for
 *  this equipment's category, falling back to the active default server-side. */
export interface ChecklistTemplate {
  template_id: string;
  category: string | null;
  items: ChecklistItem[];
}

/**
 * Fetch the checklist template for an equipment. CONTRACT-FIRST vs BE-088:
 * `retry: false` so a pre-merge 404 (or offline error) settles fast; the
 * InspectionSheet falls back to the INSPECTION_CHECKLIST_V1 constant.
 */
export function useChecklistTemplate(id: string | undefined) {
  return useQuery({
    queryKey: [...equipmentKeys.all, 'checklist-template', id ?? ''],
    queryFn: async () =>
      (await api.get<ApiResponse<ChecklistTemplate>>(`/api/v1/equipment/${id}/checklist-template`)).data,
    enabled: !!id,
    retry: false,
  });
}

/** Field walk-around submit; server dedupes replays on (equipment_id, offline_id). */
export interface CreateInspectionData {
  type: 'inspection';
  performed_at: string;
  result: 'pass' | 'fail' | 'needs_attention';
  checklist: { item: string; ok: boolean; note?: string }[];
  offline_id: string;
  take_out_of_service?: boolean;
  /** Section first (2026-10-19): on a job with sections, the section the machine is working in. */
  project_area_id?: string | null;
}

export function useCreateInspection(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateInspectionData) =>
      (await api.post<ApiResponse<EquipmentInspection>>(`/api/v1/equipment/${id}/inspections`, data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: equipmentKeys.all }),
  });
}
